import { Formik } from "formik";
import { PDFDocument } from "pdf-lib"; // ✅ Import PDF library
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as Yup from "yup";
import adminApi from "../../../api/adminApi";
import adminCompanyCreditApi from "../../../api/adminCompanyCredit";
import companyApi from "../../../api/company";
import companyUserApi from "../../../api/companyUsers";
import creditRequestApi from "../../../api/creditRequestApi";
import documentApi from "../../../api/documentapi";
import UserFrymes from "../utils/UserFrymes";
import "./newContent.css";

const DocumentDetails = ({ onNext, formData, setFormData, userCredit }) => {
  const navigate = useNavigate();
  const [userCredits, setUserCredits] = useState([]);
  const [companyCredits, setCompanyCredits] = useState({});
  const [additionalFiles, setAdditionalFiles] = useState([]); // ✅ Optional files list
  const [addingFile, setAddingFile] = useState(false); // ✅ Control visibility of file input
  const [tempFile, setTempFile] = useState(null); // ✅ Store temporary selected optional file
  const [docCost, setDocCost] = useState(0);
  const [signCost, setSignCost] = useState(0);
  const user = JSON.parse(localStorage.getItem("user"));
  const userEmail = user?.userEmail;
  const [showReviewerEmail, setShowReviewerEmail] = useState(false);
  const [creditSource, setCreditSource] = useState("user"); // "user" or "company"
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const location = useLocation();
  const { documentName, file, fromTemplate } = location.state || {};

  const basicFormSchema = Yup.object().shape({
    documentName: Yup.string().required("Document Name is required"),
    description: Yup.string(),
    file: Yup.mixed().required("A file is required"),
  });

  useEffect(() => {
    const fetchUserCompanies = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;

        const response = await companyApi.getListOfCompanyByAdminEmail(
          user.userEmail,
        );
        setUserCompanies(response.data || []);

        const assignedResponse =
          await companyUserApi.getAssignedCompaniesFromAdmin(user.userEmail);
        setAssignedCompanies(assignedResponse.data || []);
      } catch (error) {
        console.error("Error fetching user companies:", error);
      }
    };

    fetchUserCompanies();
  }, []);

  const mergedCompanies = [
    ...userCompanies.map((c) => ({ ...c, type: "owned" })),
    ...assignedCompanies.map((c) => ({ ...c, type: "assigned" })),
  ];
  useEffect(() => {
    adminApi
      .getCreditSettings()
      .then((res) => {
        setDocCost(res.data.docCost);
        setSignCost(res.data.signCost);
      })
      .catch((err) => {
        console.error("Failed to load credit settings", err);
        Swal.fire(
          "Error",
          "Unable to load credit settings from server.",
          "error",
        );
      });
  }, []);

  useEffect(() => {
    fetchUserCredits();
  }, []);

  const fetchUserCredits = async () => {
    try {
      const response = await creditRequestApi.getUserCredits(userEmail);
      setUserCredits(response.data);
    } catch (error) {
      console.error("Error fetching user credits", error);
    }
  };

  useEffect(() => {
    const fetchCompanyCreditsByCompany = async () => {
      if (!selectedCompany) return;

      try {
        const response =
          await adminCompanyCreditApi.getCompanyCreditsByCompany(
            selectedCompany,
          );
        setCompanyCredits(response.data);
      } catch (error) {
        console.error("Error fetching company credits by company:", error);
      }
    };

    fetchCompanyCreditsByCompany();
  }, [selectedCompany]);

  const mergeFiles = async (primaryFile, optionalFiles) => {
    const mergedPdf = await PDFDocument.create();

    const allFiles = [primaryFile, ...optionalFiles];

    for (const file of allFiles) {
      if (!file) continue;
      const pdfBytes = await file.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    const mergedBytes = await mergedPdf.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  };
  const documentCharge = docCost;
  const signatoryCharge = signCost;
  const handleSaveDraft = async (values) => {
    try {
      const mergedFile = await mergeFiles(values.file, additionalFiles);

      const payload = {
        senderEmail: userEmail,
        documentName: values.documentName,
        description: values.description || "",
        fileName: values.file?.name || "",
        signingMode: null,
        additionalInitials: false,
        deadline: null,
        reminderEveryDay: false,
        reminderDaysBeforeEnabled: false,
        reminderDaysBefore: null,
        reminderLastDay: false,
        sendFinalCopy: false,
        documentCharge,
        signatoryCharge,
        totalCredits: 0,
        draft: true,
        signers: [],
        reviewers: reviewers
          .filter((r) => r.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email))
          .map((r) => ({ reviewerEmail: r.email })),
      };

      const formDataToSend = new FormData();
      formDataToSend.append(
        "data",
        new Blob([JSON.stringify(payload)], { type: "application/json" }),
      );
      formDataToSend.append("file", mergedFile);

      const response = await documentApi.saveDocument(formDataToSend);

      if (response.status === 200) {
        await Swal.fire({
          icon: "success",
          title: "Draft Saved!",
          text: "Your document draft has been saved successfully.",
          confirmButtonText: "OK",
        });
        localStorage.setItem("consentsActiveTab", "drafts");
        navigate("/dashboard/my-consents");
      } else {
        await Swal.fire({
          icon: "error",
          title: "Save Failed",
          text: "Something went wrong while saving the draft.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred. Please try again later.",
        confirmButtonText: "OK",
      });
    }
  };

  const addReviewer = () => {
    const email = reviewerEmail.trim().toLowerCase();

    if (!isValidEmail(email)) {
      Swal.fire("Invalid Email", "Please enter a valid email.", "error");
      return;
    }

    const isDuplicate = reviewers.some(
      (r) => r.email.trim().toLowerCase() === email,
    );

    if (isDuplicate) {
      Swal.fire({
        icon: "error",
        title: "Duplicate Reviewer",
        text: "This reviewer email has already been added.",
      });
      return;
    }

    setReviewers([...reviewers, { email }]);
    setReviewerEmail("");
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleDeleteReviewer = (index) => {
    const updated = [...reviewers];
    updated.splice(index, 1);
    setReviewers(updated);
  };

  return (
    <>
      <div>
        <Row>
          <Col md={4}>
            <h3>
              <strong>Document Details</strong>
            </h3>
            <p>Provide basic information about your consent document</p>
          </Col>
          <Col md={8} className="text-md-end text-start">
            {creditSource === "company" ? (
              <p>
                <strong>Free frymes:</strong> {companyCredits?.freeCreidts || 0}
                &nbsp;||&nbsp;
                <strong>Total paid frymes:</strong>{" "}
                {companyCredits?.paidcredits || 0}
                &nbsp;||&nbsp;
                <strong>Balance paid frymes:</strong>{" "}
                {companyCredits?.balanceCredit || 0}
                &nbsp;||&nbsp;
                <strong>Used frymes:</strong> {companyCredits?.usedCredit || 0}
              </p>
            ) : (
              <UserFrymes userCredits={userCredits} />
            )}
          </Col>
        </Row>
      </div>

      <Card className="p-4">
        <Formik
          initialValues={{
            documentName: documentName || formData.documentName || "",
            description: formData.description || "",
            file: file || formData.file || null,
          }}
          validationSchema={basicFormSchema}
          onSubmit={async (values) => {
            if (
              creditSource === "user" &&
              (userCredits?.balanceCredit || userCredits?.freeFrymesBalance) < 1
            ) {
              Swal.fire({
                icon: "warning",
                title: "Oops!",
                html: `
      You need at least 1 credit to proceed.<br/>
      Please add credits to your account and try again.
    `,
                showCancelButton: true,
                confirmButtonText: "Upgrade Credits",
                cancelButtonText: "Cancel",
              }).then((result) => {
                if (result.isConfirmed) {
                  navigate("/dashboard/creditRequest?tab=user");
                }
              });
              return;
            }

            const mergedFile = await mergeFiles(values.file, additionalFiles);

            const finalValues = {
              ...values,
              file: mergedFile,
              creditSource,
              companyName: creditSource === "company" ? selectedCompany : null,
              reviewers,
            };
            setFormData((prev) => ({ ...prev, ...finalValues }));
            onNext();
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <h5>Choose Credit Source</h5>
              <Form.Check
                type="radio"
                label="Use My frymes"
                name="creditSource"
                value="user"
                checked={creditSource === "user"}
                onChange={(e) => setCreditSource(e.target.value)}
              />
              <Form.Check
                type="radio"
                label="Use Company frymes"
                name="creditSource"
                value="company"
                checked={creditSource === "company"}
                onChange={(e) => setCreditSource(e.target.value)}
                disabled={mergedCompanies.length === 0}
              />

              {creditSource === "company" && mergedCompanies.length === 0 && (
                <div className="mt-3 text-danger">
                  <strong>Sorry!</strong> You don’t have access to any company
                  accounts.
                  <br />
                  You are not eligible to use company credits. Please contact
                  your administrator if you believe this is an error.
                </div>
              )}

              {creditSource === "company" && mergedCompanies.length > 0 && (
                <Form.Group controlId="selectCompany" className="mt-3">
                  <Form.Label>Select Company</Form.Label>
                  <Form.Select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">-- Select Company --</option>
                    {mergedCompanies.map((company) => (
                      <option key={company.id} value={company.companyName}>
                        {company.companyName} - {company.location}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group controlId="documentName" className="mb-3">
                <Form.Label className="required-label">
                  Document Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="documentName"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.documentName}
                  isInvalid={touched.documentName && errors.documentName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.documentName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="description" className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.description}
                  isInvalid={touched.description && errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="file" className="mb-3">
                <Form.Label className="required-label">
                  Upload Document
                </Form.Label>

                <Form.Control
                  type="file"
                  name="file"
                  accept="application/pdf"
                  onChange={(event) => {
                    const selectedFile = event.currentTarget.files[0];

                    if (!selectedFile) return;

                    const hasPaidCredits =
                      (userCredits?.balanceCredit || 0) > 0;

                    const maxFileSize = hasPaidCredits
                      ? 10 * 1024 * 1024 // 10 MB
                      : 2 * 1024 * 1024; // 2 MB

                    const maxFileSizeText = hasPaidCredits ? "10 MB" : "2 MB";

                    if (selectedFile.size > maxFileSize) {
                      Swal.fire({
                        icon: "error",
                        title: "File Too Large",
                        text: `Please upload a PDF smaller than ${maxFileSizeText}.`,
                      });

                      event.target.value = "";
                      return;
                    }

                    setFieldValue("file", selectedFile);
                  }}
                />

                {fromTemplate && values.file && (
                  <div className="mt-2 text-success">
                    ✔ File already added from template:{" "}
                    <strong>{values.file.name}</strong>
                  </div>
                )}

                {!fromTemplate && values.file && (
                  <div className="mt-2 text-success">
                    Selected file: <strong>{values.file.name}</strong>
                  </div>
                )}

                <Form.Control.Feedback type="invalid">
                  {errors.file}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="addReviewerCheckbox" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Add Reviewer Email"
                  checked={showReviewerEmail}
                  onChange={() => setShowReviewerEmail((prev) => !prev)}
                />
              </Form.Group>
              {showReviewerEmail && (
                <Form.Group controlId="reviewerEmail" className="mb-3">
                  <Form.Label>Reviewer Email</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="email"
                      placeholder="Enter reviewer email"
                      value={reviewerEmail}
                      onChange={(e) => {
                        const email = e.target.value;
                        const normalizedEmail = email.trim().toLowerCase();

                        const isDuplicate = reviewers.some(
                          (r) =>
                            r.email.trim().toLowerCase() === normalizedEmail,
                        );

                        if (isDuplicate) {
                          Swal.fire({
                            icon: "error",
                            title: "Duplicate Reviewer",
                            text: "This reviewer email has already been added.",
                          });

                          setReviewerEmail("");
                          return;
                        }

                        setReviewerEmail(email);
                      }}
                      // required={showReviewerEmail}
                      isInvalid={
                        reviewerEmail !== "" && !isValidEmail(reviewerEmail)
                      }
                    />
                    <Button variant="info" onClick={addReviewer}>
                      Add
                    </Button>
                  </div>
                </Form.Group>
              )}
              {reviewers.length > 0 && (
                <div className="mt-3">
                  <h6>Reviewers:</h6>
                  <ul>
                    {reviewers.map((reviewer, index) => (
                      <li key={index}>
                        <span>{reviewer.email}</span>
                        <FaTrash
                          style={{ cursor: "pointer", color: "red" }}
                          onClick={() => handleDeleteReviewer(index)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ✅ Additional Files UI */}
              <div className="mb-3">
                <Button
                  variant="info"
                  onClick={() => setAddingFile(true)}
                  disabled={addingFile}
                >
                  Add New
                </Button>
              </div>
              {addingFile && (
                <div className="mb-3 border p-3  rounded">
                  <Form.Group>
                    <Form.Label>Upload Additional Document</Form.Label>
                    <Form.Control
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => {
                        const selectedFile = e.currentTarget.files[0];

                        if (!selectedFile) return;

                        const hasPaidCredits =
                          (userCredits?.balanceCredit || 0) > 0;

                        const maxFileSize = hasPaidCredits
                          ? 10 * 1024 * 1024
                          : 2 * 1024 * 1024;

                        const maxFileSizeText = hasPaidCredits
                          ? "10 MB"
                          : "2 MB";
                        if (selectedFile.size > maxFileSize) {
                          Swal.fire({
                            icon: "error",
                            title: "File Too Large",
                            text: `Please upload a PDF smaller than ${maxFileSizeText}.`,
                          });

                          e.target.value = "";
                          return;
                        }

                        setTempFile(selectedFile);
                      }}
                    />
                  </Form.Group>
                  <div className="mt-2">
                    <Button
                      variant="success"
                      className="me-2"
                      onClick={() => {
                        if (tempFile) {
                          setAdditionalFiles((prev) => [...prev, tempFile]);
                          setTempFile(null);
                          setAddingFile(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        setTempFile(null);
                        setAddingFile(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {/* ✅ Show optional files list */}
              {additionalFiles.length > 0 && (
                <div className="mb-3">
                  <strong>Additional Documents:</strong>
                  <ul>
                    {additionalFiles.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-end">
                <Button
                  variant="secondary"
                  className="me-2"
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, ...values }));
                    handleSaveDraft(values);
                  }}
                >
                  Save as Draft
                </Button>
                <Button type="submit" variant="primary">
                  Next
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    </>
  );
};

export default DocumentDetails;
